import { useCallback, useEffect, useRef, useState } from "react";
import {
  Ban, CalendarClock, CheckCircle2, Eye, Hourglass, Pencil, RotateCcw, Send, ShieldCheck, ThumbsDown, ThumbsUp,
  Unlock, Users, XCircle,
} from "lucide-react";
import Modal from "../Modal";
import Btn from "../Btn";
import Select2 from "../Select2";
import { todoTaskApi } from "../../api/todoTaskApi";
import TodoTaskFormModal from "./TodoTaskFormModal";
import TodoPdfMenu, { taskAttachments, taskPdfName } from "./TodoPdfMenu";
import {
  DueTag, Field, GroupTag, ImageTray, Lightbox, OutsideTag, PhotoRow, PriorityTag, PromisedTag, STATUS, SeenList,
  StatusChip, errMsg, eventBody, eventLine, fmtDateTime, fmtDay, inputStyle, isSelfTask, localIsoDate,
  notifyTodoChanged, unwrap, useStagedFiles,
} from "./todoShared";

/**
 * One task: who, what, its whole history with files, who has seen it,
 * and the steps THIS viewer may take right now (the server's
 * `allowedActions` — never derived here).
 *
 *  - Assignee, first: Accept (+ "I will complete it by" date) · Decline (why)
 *  - Assignee, once accepted: Mark done (note + files) · Pending on someone
 *    (who + why) · Resume · Change date (new date + why)
 *  - The person it is waiting on: My part is done
 *  - Whoever assigned it: Verify · Send back (why) · Edit · Cancel; a
 *    declined task: Offer again (edit — same person or another) or Cancel;
 *    a finished or cancelled one: Reopen (same person or another)
 *  - A message or file on a verified task reopens it (server side)
 *  - Everyone who can see it: a comment with photos
 *  - Outside the factory: whoever tracks it marks it Done or Not done
 *
 * A copy of a task given to several people lists the other copies; the
 * ones this viewer may see open in place.
 *
 * PDF ▾ — the task as a PDF (details, history with photos, seen, files):
 * open, download, or share it with every attached file.
 */
export default function TodoTaskDetailModal({ taskId, open, onClose, onChanged }) {
  // The copy on screen — starts as the one asked for, may move to a sibling.
  const [viewId, setViewId] = useState(taskId);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("COMMENT");
  const [note, setNote] = useState("");
  const [blockedById, setBlockedById] = useState("");
  const [reopenTo, setReopenTo] = useState("");
  const [promised, setPromised] = useState("");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [editing, setEditing] = useState(false);
  const staged = useStagedFiles();

  // Only the latest request may fill the window (a sibling clicked mid-load).
  const reqRef = useRef(0);
  const load = useCallback(async () => {
    if (!viewId) return;
    const mine = ++reqRef.current;
    setLoading(true);
    setError("");
    try {
      const t = unwrap(await todoTaskApi.get(viewId));
      if (mine !== reqRef.current) return;
      setTask(t);
      // Opening marks it read — the badges drop it from "new".
      notifyTodoChanged();
    } catch (e) {
      if (mine === reqRef.current) setError(errMsg(e, "Could not open the task"));
    } finally {
      if (mine === reqRef.current) setLoading(false);
    }
  }, [viewId]);

  // Opening (or being pointed at another task) starts on that task; closing
  // forgets it, so the next open never shows the previous one first.
  useEffect(() => {
    setViewId(open ? taskId : null);
  }, [open, taskId]);

  useEffect(() => {
    if (!open || !viewId) return;
    setTask(null);
    // Photos staged for another copy must not ride along to this one.
    staged.discardAll();
    resetComposer();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, viewId]);

  function resetComposer() {
    setMode("COMMENT");
    setNote("");
    setBlockedById("");
    setReopenTo("");
    setPromised("");
    setError("");
  }

  const close = () => {
    if (busy) return;
    staged.discardAll();
    onClose();
  };

  const today = localIsoDate(new Date());

  const startMode = async (m) => {
    setError("");
    if ((m === "BLOCK" || (m === "REOPEN" && !task?.outside)) && users.length === 0) {
      try { setUsers(unwrap(await todoTaskApi.assignableUsers()) || []); } catch { /* picker stays empty */ }
    }
    // Accepting starts from the due date (when it is still ahead); he can pick another.
    if (m === "ACCEPT") setPromised(task?.dueDate && task.dueDate >= today ? task.dueDate : "");
    if (m === "CHANGE_DATE") setPromised("");
    if (m === "REOPEN") setReopenTo(task?.assignedToId ? String(task.assignedToId) : "");
    setMode(m);
  };

  const submit = async (action) => {
    if (!task) return;
    const body = note.trim();
    const dated = action === "ACCEPT" || action === "CHANGE_DATE";
    if (action === "COMMENT" && !body && staged.images.length === 0) { setError("Write something or attach a file"); return; }
    if (dated && !promised) { setError("Choose the date you will complete it by"); return; }
    if (dated && promised < today) { setError("The date cannot be in the past"); return; }
    if (action === "CHANGE_DATE" && promised === task.promisedDate) { setError("That is already the date — choose another one"); return; }
    if (action === "CHANGE_DATE" && task.promisedDate && !body) { setError("Say why the date is changing"); return; }
    if (action === "DECLINE" && !body) { setError("Say why you cannot take this task"); return; }
    if (action === "BLOCK" && !blockedById) { setError("Choose who the work is waiting on"); return; }
    if (action === "BLOCK" && !body) { setError("Say what you are waiting for"); return; }
    if (action === "SEND_BACK" && !body) { setError("Say what still needs doing"); return; }
    if (action === "CANCEL" && !window.confirm(`Cancel ${task.taskNumber}? It stays in the history but nobody has to do it.`)) return;
    setBusy(true);
    setError("");
    try {
      const updated = unwrap(await todoTaskApi.act(task.id, {
        action,
        body: body || null,
        promisedDate: dated ? promised : null,
        blockedById: action === "BLOCK" ? Number(blockedById) : null,
        assignedToId: action === "REOPEN" && reopenTo ? Number(reopenTo) : null,
        attachmentIds: staged.images.map((i) => i.id),
      }));
      staged.clear();
      setTask(updated);
      resetComposer();
      notifyTodoChanged();
      onChanged?.(updated);
    } catch (e) {
      setError(errMsg(e, "Could not save"));
    } finally {
      setBusy(false);
    }
  };

  const allowed = new Set(task?.allowedActions || []);
  const iAmAssignee = task && task.myRole !== "BLOCKER" && (task.myRole === "ASSIGNEE" || task.myRole === "SELF");

  // The buttons — only what the server allows.
  const buttons = [
    allowed.has("ACCEPT") && { key: "ACCEPT", label: "Accept", icon: <ThumbsUp size={14} />, variant: "primary" },
    allowed.has("DECLINE") && { key: "DECLINE", label: "Decline", icon: <ThumbsDown size={14} />, variant: "danger" },
    allowed.has("DONE") && { key: "DONE", label: "Mark done", icon: <CheckCircle2 size={14} />, variant: "primary" },
    allowed.has("BLOCK") && { key: "BLOCK", label: task?.status === "WAITING" ? "Waiting on someone else" : "Pending on someone", icon: <Hourglass size={14} />, variant: "secondary" },
    allowed.has("UNBLOCK") && { key: "UNBLOCK", label: iAmAssignee ? "Resume work" : "My part is done", icon: <Unlock size={14} />, variant: "secondary" },
    allowed.has("CHANGE_DATE") && { key: "CHANGE_DATE", label: task?.promisedDate ? "Change date" : "Set complete-by date", icon: <CalendarClock size={14} />, variant: "secondary" },
    allowed.has("VERIFY") && { key: "VERIFY", label: "Verify", icon: <ShieldCheck size={14} />, variant: "primary" },
    allowed.has("SEND_BACK") && { key: "SEND_BACK", label: "Send back", icon: <RotateCcw size={14} />, variant: "danger" },
    allowed.has("NOT_DONE") && { key: "NOT_DONE", label: "Not done", icon: <XCircle size={14} />, variant: "danger" },
    allowed.has("CANCEL") && { key: "CANCEL", label: "Cancel task", icon: <Ban size={14} />, variant: "ghost" },
    allowed.has("REOPEN") && { key: "REOPEN", label: task?.outside ? "Reopen" : "Reopen / Re-assign", icon: <RotateCcw size={14} />, variant: "secondary" },
  ].filter(Boolean);

  const COMPOSER = {
    COMMENT: { title: null, placeholder: "Write a comment…", send: "Send", photos: true },
    ACCEPT: { title: "Accept the task", placeholder: "Note (optional)", send: "Accept", photos: true, date: "I will complete it by" },
    DECLINE: { title: "Decline the task", placeholder: "Why can't you take it? (required)", send: "Decline", photos: true },
    CHANGE_DATE: {
      title: task?.promisedDate ? "Change the complete-by date" : "Set the complete-by date",
      placeholder: task?.promisedDate ? "Why is the date changing? (required)" : "Note (optional)",
      send: task?.promisedDate ? "Change date" : "Set date", photos: true, date: "I will now complete it by",
    },
    DONE: { title: "Mark it done", placeholder: "What was done? (optional) — attach photos or files of the work", send: "Mark done", photos: true },
    BLOCK: { title: "Pending because of another user", placeholder: "What are you waiting for? (required)", send: "Mark pending", photos: true },
    UNBLOCK: { title: iAmAssignee ? "Resume the work" : "Your part is done", placeholder: "Note (optional)", send: iAmAssignee ? "Resume" : "Hand it back", photos: true },
    VERIFY: { title: "Verify the work", placeholder: "Note (optional)", send: "Verify", photos: true },
    SEND_BACK: { title: "Send it back", placeholder: "What still needs doing? (required)", send: "Send back", photos: true },
    CANCEL: { title: "Cancel the task", placeholder: "Why? (optional)", send: "Cancel task", photos: true },
    NOT_DONE: { title: "Close it as not done", placeholder: "What happened? (optional)", send: "Mark not done", photos: true },
    REOPEN: { title: "Reopen the task", placeholder: "Why is it reopened? (optional)", send: "Reopen", photos: true },
  };
  const c = COMPOSER[mode] || COMPOSER.COMMENT;
  const own = isSelfTask(task);

  return (
    <>
      <Modal open={open} onClose={close} width={760}
        title={task ? task.title : "Task"}
        subtitle={task
          ? (task.outside
            ? `${task.taskNumber} · outside the factory · ${task.outsideParty || ""}`
            : `${task.taskNumber} · ${task.assignedByName} → ${task.assignedToName}`)
          : ""}>
        {loading && !task && <div style={{ padding: 30, textAlign: "center", color: "var(--text-2)" }}>Loading…</div>}
        {!loading && !task && error && <div style={{ padding: 20, color: "var(--danger)" }}>{error}</div>}
        {task && (
          <div>
            {/* ── header ───────────────────────────────────────── */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
              <StatusChip task={task} />
              <PriorityTag priority={task.priority} />
              <DueTag task={task} />
              <PromisedTag task={task} />
              <OutsideTag task={task} />
              <GroupTag task={task} />
              {task.reopenCount > 0 && (
                <span style={{ fontSize: 11, color: "var(--danger)", fontWeight: 600 }}>Sent back ×{task.reopenCount}</span>
              )}
              <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
                <TodoPdfMenu size="xs"
                  getPdf={async () => ({ blob: (await todoTaskApi.taskPdf(task.id)).data, name: taskPdfName(task) })}
                  attachments={taskAttachments(task)}
                  shareTitle={`${task.taskNumber} — ${task.title}`}
                  shareText={`To-do ${task.taskNumber}: ${task.title}`} />
                {task.canEdit && (
                  <Btn variant="ghost" size="xs" icon={<Pencil size={12} />} onClick={() => setEditing(true)}>
                    Edit
                  </Btn>
                )}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "6px 16px", fontSize: 13, marginBottom: 12 }}>
              <Info label={task.outside ? "Tracked by" : "Assigned by"} value={task.assignedByName} />
              <Info label={task.outside ? "Outside — who / where" : "Assigned to"}
                value={task.outside ? task.outsideParty : task.assignedToName} />
              {task.outside && task.outsidePartyMobile && (
                <Info label="Mobile" value={<a href={`tel:${task.outsidePartyMobile}`} style={{ color: "var(--primary)" }}>{task.outsidePartyMobile}</a>} />
              )}
              <Info label="Created" value={fmtDateTime(task.createdAt)} />
              {!task.outside && !own && task.acceptedAt && <Info label="Accepted" value={fmtDateTime(task.acceptedAt)} />}
              {task.promisedDate && <Info label="Will finish by" value={fmtDay(task.promisedDate)} />}
              {task.verifiedByName && <Info label="Verified by" value={`${task.verifiedByName} · ${fmtDateTime(task.verifiedAt)}`} />}
            </div>
            {/* NOT ACCEPTED YET — what the assignee must do; for the others, whether he has seen it. */}
            {task.status === "OPEN" && !task.outside && !own && (
              <Banner tone="info">
                {iAmAssignee
                  ? <><b>{task.assignedByName} gave you this task.</b> Accept it with the date you will complete it by{task.dueDate ? ` (due ${fmtDay(task.dueDate)})` : ""}, or decline it with the reason.</>
                  : <><b>Not accepted yet</b> by {task.assignedToName} — {task.assigneeSeenAt ? `seen ${fmtDateTime(task.assigneeSeenAt)}` : "not seen yet"}.</>}
              </Banner>
            )}
            {task.status === "DECLINED" && (
              <Banner tone="danger">
                <b>{iAmAssignee ? "You declined this task" : `${task.assignedToName} declined this task`}</b>
                {task.declineReason ? ` — ${task.declineReason}` : ""}
                {task.canEdit && (
                  <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <Btn size="xs" icon={<Pencil size={12} />} onClick={() => setEditing(true)}>Offer again</Btn>
                    <span style={{ fontSize: 12 }}>to them or someone else — or cancel the task.</span>
                  </div>
                )}
              </Banner>
            )}
            {task.status === "WAITING" && (
              <Banner tone="warn">
                <b>Waiting on {task.blockedByName}</b>{task.blockedReason ? ` — ${task.blockedReason}` : ""}
              </Banner>
            )}
            {/* THE GROUP — the same task given to several people, a copy each. */}
            {Array.isArray(task.group) && task.group.length > 1 && (
              <div style={{ padding: "8px 12px", borderRadius: 8, background: "var(--bg-hover)", marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-1)", display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <Users size={13} /> Given to {task.group.length} people · {task.groupFinished} of {task.groupTotal} done
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {task.group.map((g) => {
                    const st = STATUS[g.status] || STATUS.OPEN;
                    const clickable = g.canOpen && !g.current;
                    return (
                      <button key={g.id} type="button" disabled={!clickable}
                        onClick={() => clickable && setViewId(g.id)}
                        title={clickable ? `Open ${g.taskNumber}` : g.current ? "This copy" : g.taskNumber}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 8,
                          fontSize: 12, fontFamily: "inherit", cursor: clickable ? "pointer" : "default",
                          border: `1px solid ${g.current ? "var(--primary)" : "var(--border)"}`, background: "var(--bg-card)",
                          color: "var(--text-0)",
                        }}>
                        <span style={{ fontWeight: g.current ? 800 : 600 }}>{g.assignedToName}</span>
                        <span style={{ padding: "1px 6px", borderRadius: 999, fontSize: 10.5, fontWeight: 700, background: st.bg, color: st.fg }}>
                          {st.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {task.description && (
              <div style={{ whiteSpace: "pre-wrap", fontSize: 13.5, color: "var(--text-0)", padding: "10px 12px", background: "var(--bg-hover)", borderRadius: 8, marginBottom: 12 }}>
                {task.description}
              </div>
            )}

            {/* ── seen ─────────────────────────────────────────── */}
            {Array.isArray(task.seenBy) && task.seenBy.length > 0 && (
              <details style={{ marginBottom: 12, fontSize: 13 }}>
                <summary style={{ cursor: "pointer", color: "var(--text-1)", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  <Eye size={13} /> Seen by {task.seenBy.length} {task.seenBy.length === 1 ? "person" : "people"}
                  {!task.outside && !own && (
                    <span style={{ fontWeight: 500, color: "var(--text-2)" }}>
                      · {task.assignedToName} {task.assigneeSeenAt ? `opened it ${fmtDateTime(task.assigneeSeenAt)}` : "has not opened it yet"}
                    </span>
                  )}
                </summary>
                <div style={{ padding: "8px 12px", marginTop: 6, background: "var(--bg-hover)", borderRadius: 8 }}>
                  <SeenList seenBy={task.seenBy} />
                </div>
              </details>
            )}

            {/* ── history ──────────────────────────────────────── */}
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: 0.3, margin: "4px 0 8px" }}>
              History
            </div>
            <div style={{ maxHeight: 300, overflowY: "auto", paddingRight: 4, marginBottom: 12 }}>
              {task.messages.map((m) => <HistoryItem key={m.id} m={m} task={task} onOpen={setLightbox} />)}
            </div>

            {/* ── actions ──────────────────────────────────────── */}
            {buttons.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                {buttons.map((b) => (
                  <Btn key={b.key} size="sm" variant={mode === b.key ? "primary" : b.variant} icon={b.icon} disabled={busy}
                    onClick={() => (b.now ? submit(b.key) : startMode(b.key))}>
                    {b.label}
                  </Btn>
                ))}
              </div>
            )}

            {/* ── composer ─────────────────────────────────────── */}
            <div style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 12, background: "var(--bg-card)" }}>
              {c.title && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <b style={{ fontSize: 13 }}>{c.title}</b>
                  <button type="button" onClick={resetComposer} style={{ border: "none", background: "none", color: "var(--text-2)", cursor: "pointer", fontSize: 12 }}>
                    back to comment
                  </button>
                </div>
              )}
              {c.date && (
                <Field label={c.date}
                  hint={[
                    task.dueDate ? `Due date: ${fmtDay(task.dueDate)}` : "No due date was set",
                    promised && task.dueDate && promised > task.dueDate ? "— your date is after it" : "",
                    mode === "CHANGE_DATE" && task.promisedDate ? `· you said ${fmtDay(task.promisedDate)}` : "",
                  ].filter(Boolean).join(" ")}>
                  <input type="date" value={promised} min={today} onChange={(e) => setPromised(e.target.value)}
                    style={{ ...inputStyle, maxWidth: 220 }} />
                </Field>
              )}
              {mode === "REOPEN" && !task.outside && (
                <Field label="Give it to" hint="The same person gets it back; someone else has to accept it first.">
                  <Select2 value={reopenTo} onChange={(v) => setReopenTo(v ? String(v) : "")} placeholder="Choose the person…"
                    options={users.map((u) => ({ value: String(u.id), label: u.id === task.assignedToId ? `${u.name} (same person)` : u.name }))} />
                </Field>
              )}
              {mode === "BLOCK" && (
                <Field label="Waiting on">
                  <Select2 value={blockedById} onChange={(v) => setBlockedById(v ? String(v) : "")} placeholder="Choose the person…"
                    options={users.filter((u) => u.id !== task.assignedToId).map((u) => ({ value: String(u.id), label: u.name }))} />
                </Field>
              )}
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={mode === "COMMENT" ? 2 : 3}
                placeholder={c.placeholder} style={{ ...inputStyle, resize: "vertical", marginBottom: 8 }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {c.photos ? <ImageTray staged={staged} onOpen={setLightbox} disabled={busy} /> : <span />}
                <Btn size="sm" icon={<Send size={13} />} disabled={busy || staged.uploading > 0}
                  variant={mode === "SEND_BACK" || mode === "NOT_DONE" || mode === "DECLINE" ? "danger" : "primary"} onClick={() => submit(mode)}>
                  {busy ? "Saving…" : c.send}
                </Btn>
              </div>
              {error && (
                <div style={{ marginTop: 8, padding: "7px 10px", borderRadius: 8, background: "var(--danger-bg)", color: "var(--danger)", fontSize: 12.5 }}>
                  {error}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
      <TodoTaskFormModal open={editing} task={task} onClose={() => setEditing(false)}
        onSaved={(t) => { setEditing(false); setTask(t); onChanged?.(t); }} />
      <Lightbox url={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}

function Banner({ tone, children }) {
  return (
    <div style={{
      padding: "8px 12px", borderRadius: 8, fontSize: 13, marginBottom: 12,
      background: `var(--${tone}-bg)`, color: `var(--${tone})`,
    }}>{children}</div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ color: "var(--text-0)" }}>{value || "—"}</div>
    </div>
  );
}

const KIND_COLOR = {
  DONE: "var(--success)", VERIFIED: "var(--success)", SENT_BACK: "var(--danger)", CANCELLED: "var(--text-2)",
  BLOCKED: "var(--warn)", UNBLOCKED: "var(--info)", ACCEPTED: "var(--success)", DECLINED: "var(--danger)",
  DATE_CHANGED: "var(--warn)", NOT_DONE: "var(--danger)",
};

function HistoryItem({ m, task, onOpen }) {
  if (m.kind === "COMMENT") {
    return (
      <div style={{ display: "flex", justifyContent: m.mine ? "flex-end" : "flex-start", marginBottom: 8 }}>
        <div style={{
          maxWidth: "80%", padding: "8px 11px", borderRadius: 10, fontSize: 13,
          background: m.mine ? "var(--primary-dim, var(--info-bg))" : "var(--bg-hover)",
        }}>
          <div style={{ fontSize: 11, color: "var(--text-2)", marginBottom: 2 }}>
            <b style={{ color: "var(--text-1)" }}>{m.mine ? "You" : m.authorName}</b> · {fmtDateTime(m.createdAt)}
          </div>
          {m.body && <div style={{ whiteSpace: "pre-wrap", color: "var(--text-0)" }}>{m.body}</div>}
          <PhotoRow attachments={m.attachments} onOpen={onOpen} />
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 12.5 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", marginTop: 5, flexShrink: 0, background: KIND_COLOR[m.kind] || "var(--primary)" }} />
      <div style={{ flex: 1 }}>
        <span style={{ fontWeight: 600, color: KIND_COLOR[m.kind] || "var(--text-1)" }}>{eventLine(m, task)}</span>
        <span style={{ color: "var(--text-2)" }}> · {fmtDateTime(m.createdAt)}</span>
        {eventBody(m) && <div style={{ whiteSpace: "pre-wrap", color: "var(--text-0)", marginTop: 2 }}>{eventBody(m)}</div>}
        <PhotoRow attachments={m.attachments} onOpen={onOpen} />
      </div>
    </div>
  );
}
